import {
  useCallback,
  useMemo,
  useRef,
  type ChangeEvent,
  type ClipboardEvent,
  type ReactNode,
} from "react";

import { filesFromClipboard } from "./chatAttachmentInputUtils";
import { FileInput } from "./controls";

export type AttachmentInputSource = "paste" | "picker";

export interface AttachmentPickerOptions {
  accept?: string;
  multiple?: boolean;
  position?: number;
}

export interface AttachmentInputContext {
  position: number | undefined;
  source: AttachmentInputSource;
}

export interface ChatAttachmentInputRenderProps {
  openFilePicker: (options?: AttachmentPickerOptions) => void;
}

export interface ChatAttachmentInputProps {
  accept?: string;
  children: (props: ChatAttachmentInputRenderProps) => ReactNode;
  className?: string;
  disabled?: boolean;
  maxFiles?: number;
  multiple?: boolean;
  onFiles: (files: File[], context: AttachmentInputContext) => void;
}

export function ChatAttachmentInput({
  accept,
  children,
  className,
  disabled = false,
  maxFiles = Number.POSITIVE_INFINITY,
  multiple = true,
  onFiles,
}: ChatAttachmentInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerPositionRef = useRef<number | undefined>(undefined);

  const deliver = useCallback(
    (files: File[], context: AttachmentInputContext) => {
      if (disabled || files.length === 0) return;
      const accepted = files.slice(0, Math.max(0, maxFiles));
      if (accepted.length > 0) onFiles(accepted, context);
    },
    [disabled, maxFiles, onFiles],
  );

  const openFilePicker = useCallback(
    (options?: AttachmentPickerOptions) => {
      const input = inputRef.current;
      if (!input || disabled) return;
      input.accept = options?.accept ?? accept ?? "";
      input.multiple = options?.multiple ?? multiple;
      pickerPositionRef.current = options?.position;
      input.click();
    },
    [accept, disabled, multiple],
  );

  const renderProps = useMemo(() => ({ openFilePicker }), [openFilePicker]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    const context: AttachmentInputContext = {
      position: pickerPositionRef.current,
      source: "picker",
    };
    pickerPositionRef.current = undefined;
    event.currentTarget.value = "";
    deliver(files, context);
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if (disabled || maxFiles <= 0) return;
    const files = filesFromClipboard(event.clipboardData, accept);
    if (files.length === 0) return;
    event.preventDefault();
    const target = event.target;
    deliver(files, {
      position:
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement
          ? (target.selectionStart ?? undefined)
          : undefined,
      source: "paste",
    });
  };

  return (
    <div className={className} onPaste={handlePaste}>
      <FileInput
        accept={accept}
        aria-hidden="true"
        disabled={disabled}
        ref={inputRef}
        multiple={multiple}
        onChange={handleChange}
        hidden
        tabIndex={-1}
      />
      {/* eslint-disable-next-line react-hooks/refs -- The render prop receives an event handler; refs are accessed only when the picker opens or changes. */}
      {children(renderProps)}
    </div>
  );
}
